/* eslint-disable no-param-reassign, no-unused-vars, prefer-destructuring */
import _ from 'underscore';
import {
  PropertyDefinition,
  MongoPredicate,
  LegacyPredicate,
  ConversionFunctionFactoryParams,
  ConversionFunction,
  LegacyConversionFunction,
  IFilterSchema,
  FilterProps,
  ValueTypeOperatorsMapping,
  EntitySchema,
  FieldType,
  FilterValue,
  PropertyAlias,
} from '../types';

const operatorMapping: ValueTypeOperatorsMapping = {
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
  integer: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
  array: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
  string: ['contains', 'doesnotcontain', 'startswith', 'endswith', 'eq', 'neq'],
  enum: ['in'],
  boolean: ['eq'],
  datetime: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'],
  id: ['in'],
};

function getSelfFilterableProps(schema: EntitySchema): FilterProps {
  return _.chain(schema.properties)
    .pairs()
    .filter((pair: [string, PropertyDefinition]) => pair[1] && pair[1].filterable === true)
    .object()
    .value();
}

function getDeepFilterableProps(schema: EntitySchema): FilterProps {
  return _.chain(schema.properties)
    .pairs()
    .filter((pair: [string, PropertyDefinition]) => pair[1].type && pair[1].type.toLowerCase() === 'object' && pair[1].schema && pair[1].filterable)
    .map((pair: [string, PropertyDefinition]) => {
      const parentKey = pair[0];

      return _.chain(pair[1].schema!.properties)
        // pick only filterable properties
        .pick(pair[1].filterable as string[])
        .pairs()
        .map((childPair: [string, PropertyDefinition]) => {
          // set alias on property object
          const prop = _.extend({}, childPair[1], {
            alias: pair[1].alias && (pair[1].alias as Record<string, PropertyAlias>)[childPair[0]],
          });
          // return key-value pair with key = parent/child
          return [parentKey.concat('.', childPair[0]), prop];
        })
        .value();
    })
    .flatten(true)
    .object()
    .value();
}

function buildAliasToProp(): Record<string, string> {
  const self: IFilterSchema = this as IFilterSchema;

  return _.chain(self.filterProps())
    .pairs()
    .map((pair: [string, PropertyDefinition]) => _.map(_.pairs(pair[1].alias || {}), (childPair: [string, string]) => [childPair[0].concat('.', childPair[1]), pair[0]]))
    .flatten(true)
    .object()
    .value();
}

function buildPropToAlias(): Record<string, string> {
  const self: IFilterSchema = this as IFilterSchema;

  return _.chain(self.aliasToProp())
    .pairs()
    .map((pair: [string, string]) => {
      const dot = pair[0].indexOf('.');
      const aliasType = pair[0].substring(0, dot);
      const alias = pair[0].substring(dot + 1);
      return [aliasType.concat('.', pair[1]), alias];
    })
    .object()
    .value();
}

function convertValue(srcPropType: string, targetPropType: FieldType, predValue: FilterValue): FilterValue {
  const srcType = srcPropType.toLowerCase();
  const targetType = targetPropType.toLowerCase();
  // only convert some types
  if (srcType === 'number' && targetType === 'integer') {
    return Math.round(predValue as number);
  }
  return predValue;
}

const conversionFunctionFactory = ({
  sourcePropertyType,
  targetPropName,
  targetProp,
}: ConversionFunctionFactoryParams): ConversionFunction => {
  const conversionFunc: ConversionFunction = (mongoPredicate: MongoPredicate) => {
    const exprObj = _.values(mongoPredicate)[0];
    const predOperator = _.keys(exprObj)[0];
    const predValue = _.values(exprObj)[0];

    // All we need here is the type for src
    const convertedValue = convertValue(sourcePropertyType, targetProp.type!, predValue);

    return {
      [targetPropName]: {
        [predOperator]: convertedValue,
      },
    };
  };
  return conversionFunc;
};

const legacyConversionFunctionFactory = ({
  sourcePropertyType,
  targetPropName,
  targetProp,
}: ConversionFunctionFactoryParams): LegacyConversionFunction => {
  const conversionFunc: LegacyConversionFunction = (legacyPredicate: LegacyPredicate) => {
    const predValue = legacyPredicate.Values[0];

    const convertedValue = convertValue(sourcePropertyType, targetProp.type!, predValue);

    legacyPredicate.SelectedColumn = targetPropName;
    legacyPredicate.Values = [convertedValue];
  };
  return conversionFunc;
};

function buildPropToConversions<TFunction extends ConversionFunction | LegacyConversionFunction>(
  isForLegacyFilter: boolean,
  conversionFuncFactory: (params: ConversionFunctionFactoryParams) => TFunction
): Record<string, TFunction> {
  const self = this as IFilterSchema;

  return _.chain(self.entitySchema.properties)
    .pairs()
    .map(([targetPropName, targetProp]: [string, PropertyDefinition]) => {
      // Only one property conversion are supported for now.
      if (targetProp.alias && (targetProp.alias as PropertyAlias).propertyConversion) {
        // `conversionSrc` must have `sourcePropertyName` and `sourcePropertyType`.
        // `sourcePropertyName` is long name with '' in b/w parentName and property name.
        // `sourcePropertyLegacyAlias` is the optional legacy alias of the source property.
        const { sourcePropertyType, sourcePropertyName, sourcePropertyLegacyAlias } = (targetProp.alias as PropertyAlias).propertyConversion!;

        if (!sourcePropertyName) {
          throw new Error('Must provide a sourcePropertyName');
        }
        if (!sourcePropertyType) {
          throw new Error('Must provide a sourcePropertyType');
        }

        const conversionFunc = conversionFuncFactory({
          sourcePropertyType,
          targetPropName,
          targetProp,
        });
        const finalSourcePropertyName = (isForLegacyFilter && sourcePropertyLegacyAlias) || sourcePropertyName;

        return [finalSourcePropertyName, conversionFunc];
      }
      return null;
    })
    .compact()
    .object()
    .value() as Record<string, TFunction>;
}

/**
 * FilterSchema is built upon entity schema.
 * It's used to get filterable properties, get alias using alias type & property key and vice versa.
 */
export class FilterSchema implements IFilterSchema {
  aliasToProp: () => Record<string, string>;

  entityName: string;

  entitySchema: EntitySchema;

  filterProps: () => FilterProps;

  propToAlias: () => Record<string, string>;

  propToConversions: () => Record<string, ConversionFunction>;

  propToLegacyConversions: () => Record<string, LegacyConversionFunction>;

  /**
   * Constructs a new instance of FilterSchema over the passed in entitySchema.
   * @param {IFilterSchema} entitySchema - the original entity schema object
   */
  constructor(entitySchema: EntitySchema) {
    this.entitySchema = entitySchema;
    this.entityName = this.entitySchema.name!;

    this.filterProps = () => _.extend(
      getSelfFilterableProps(this.entitySchema),
      getDeepFilterableProps(this.entitySchema)
    );
    this.aliasToProp = _.once(buildAliasToProp.bind(this));
    this.propToAlias = _.once(buildPropToAlias.bind(this));
    this.propToConversions = _.once(buildPropToConversions.bind(this, false, conversionFunctionFactory));
    this.propToLegacyConversions = _.once(buildPropToConversions.bind(this, true, legacyConversionFunctionFactory));
  }

  /**
   * Get entity schema's name.
   * @return {string} - the entity schema's name.
   */
  getEntityName(): string {
    return this.entityName;
  }

  /**
   * Get keys of filterable properties.
   * @return {string[]} - array of filterable properties' keys.
   */
  getPropertyKeys(): string[] {
    return _.keys(this.filterProps());
  }

  /**
   * Get filterable property using property key with formatted attributes.
   * @param {string} propertyKey - the property's key
   * @return {PropertyDefinition} - filterable property of given key, null if doesn't exist.
   */
  getFilterProperty(propertyKey: string): PropertyDefinition | null {
    const property = this.filterProps()[propertyKey];
    if (!property) {
      return null;
    }

    const type = property.type.toLowerCase() as FieldType;
    let operators: string[];

    if (!_.isEmpty(property.operators)) {
      operators = property.operators;
    } else if (_.isEmpty(property.enum)) {
      operators = operatorMapping[type];
    } else {
      operators = operatorMapping.enum;
    }

    const result: PropertyDefinition = {
      type,
      operators,
      alias: property.alias,
      name: property.name,
      description: property.description,
      isPercentValue: property.isPercentValue,
      skipLocalization: property.skipLocalization,
      isDeletedProperty: property.isDeletedProperty,
      isInvalidProperty: property.isInvalidProperty,
      title: property.title,
      localized: property.localized,
      nullable: property.nullable,
      maxLength: property.maxLength,
      minimum: property.minimum,
      maximum: property.maximum,
      pattern: property.pattern,
      customEditor: property.customEditor,
      localize: property.localize,
      enumType: property.enumType,
    };

    if (property.enum) {
      result.enum = property.enum;
    }
    if (property.chooseType) {
      result.chooseType = property.chooseType;
    }
    if (property.customParameters) {
      result.customParameters = property.customParameters;
    }
    if (property.groupedItems) {
      result.groupedItems = property.groupedItems;
    }
    if (property.hasTime) {
      result.hasTime = property.hasTime;
    }

    if (property.operatorDisplayNameMap) {
      // for customize filter operator text display
      result.operatorDisplayNameMap = property.operatorDisplayNameMap;
    }
    if (property.addtionalParams && _.isObject(property.addtionalParams)) {
      // to avoid future trouble, for non-shared params, put it here
      _.extendOwn(result, property.addtionalParams);
    }
    return result;
  }

  /**
   * Get alias using property key and alias type.
   * @param {string} propertyKey - property key.
   * @param {string} aliasType - alias type.
   * @return {string} - alias name of given key and alias type
   *                    return propertyKey if alias doesn't exist.
   */
  toAlias(propertyKey: string | null, aliasType: string | null): string | null {
    let alias: string | null = null;
    if (propertyKey && aliasType) {
      alias = this.propToAlias()[aliasType.concat('.', propertyKey)];
    }
    return alias || propertyKey;
  }

  /**
   * Get original property key using alias and alias type.
   * @param {string} alias - alias string.
   * @param {string} aliasType - type of alias.
   * @return {string} - the property key of given alias and alias type
   *                    return alias if propertyKey doesn't exist.
   */
  fromAlias(alias: string | null, aliasType: string | null): string | null {
    let propertyKey: string | null = null;
    if (alias && aliasType) {
      propertyKey = this.aliasToProp()[aliasType.concat('.', alias)];
    }
    return propertyKey || alias;
  }

  getApplicableConversions(propName: string): ConversionFunction {
    const allConversions = this.propToConversions();
    return _.isUndefined(allConversions[propName]) ? _.identity : allConversions[propName];
  }

  getApplicableLegacyConversions(propName: string): LegacyConversionFunction {
    const allConversions = this.propToLegacyConversions();
    return _.isUndefined(allConversions[propName]) ? _.identity : allConversions[propName];
  }

  applyConversions(mongoPredicate: MongoPredicate): MongoPredicate {
    const propName = _.keys(mongoPredicate)[0];
    return this.getApplicableConversions(propName)(mongoPredicate);
  }

  applyLegacyFilterConversions(legacyFilterExpressions: LegacyPredicate[]): void {
    _.each(legacyFilterExpressions, (legacyExpression: LegacyPredicate) => {
      const propName = legacyExpression.SelectedColumn;
      this.getApplicableLegacyConversions(propName)(legacyExpression);
    });
  }
}
